import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

const content = {
  ne: {
    reviewTitle: 'लेख समीक्षा',
    deleteTitle: 'लेख मेटाउनुहोस्',
    deleteConfirm: 'के तपाईं यो लेख स्थायी रूपमा मेटाउन चाहनुहुन्छ? यो कार्य फिर्ता हुन सक्दैन।',
    remarksLabel: 'टिप्पणी (अस्वीकृत गर्दा आवश्यक)',
    remarksPlaceholder: 'सुधारको लागि टिप्पणी लेख्नुहोस्...',
    remarksRequired: 'अस्वीकार गर्न टिप्पणी आवश्यक छ',
    approve: 'प्रकाशित गर्नुहोस्',
    reject: 'अस्वीकार गर्नुहोस्',
    delete: 'मेटाउनुहोस्',
    cancel: 'रद्द गर्नुहोस्',
    reporter: 'रिपोर्टर',
  },
  en: {
    reviewTitle: 'Review Article',
    deleteTitle: 'Delete Article',
    deleteConfirm: 'Are you sure you want to permanently delete this article? This action cannot be undone.',
    remarksLabel: 'Remarks (required when rejecting)',
    remarksPlaceholder: 'Write feedback for the reporter...',
    remarksRequired: 'Remarks are required to reject',
    approve: 'Publish',
    reject: 'Reject',
    delete: 'Delete',
    cancel: 'Cancel',
    reporter: 'Reporter',
  },
};

const MAX_REMARKS = 300;

const ReviewModal = ({ article, language, loading, onApprove, onReject, onDelete, onClose }) => {
  const [remarks, setRemarks] = useState('');
  const [touched, setTouched] = useState(false);
  const t = content[language] || content.en;
  const isDeleteMode = article.mode === 'delete';
  const remarksEmpty = !remarks.trim();

  const featuredImage = article.imageUrl || article.coverImage || article.image;

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [loading, onClose]);

  const handleReject = () => {
    if (remarksEmpty) {
      setTouched(true);
      return;
    }
    onReject(remarks);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-char-900/50 p-4 backdrop-blur-sm"
      style={{ animation: 'overlayIn 150ms ease-out' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-3xl bg-white shadow-2xl"
        style={{ animation: 'modalIn 180ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-char-50 px-6 py-5">
          <h3 className="text-lg font-extrabold text-char-900">
            {isDeleteMode ? t.deleteTitle : t.reviewTitle}
          </h3>
          <button
            onClick={onClose}
            disabled={loading}
            aria-label={t.cancel}
            className="rounded-xl p-1.5 text-char-400 transition-colors hover:bg-char-50 hover:text-char-600 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isDeleteMode ? (
            <div className="flex gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-500">
                <AlertTriangle size={20} />
              </span>
              <div>
                <p className="text-sm font-bold text-char-900">{article.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-char-500">{t.deleteConfirm}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {featuredImage && (
                  <img
                    src={getImageUrl(featuredImage)}
                    alt={article.title}
                    className="mb-1 w-full rounded-2xl object-cover max-h-48"
                  />
                )}
                <span className="inline-flex rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-bold text-primary">
                  {article.sector}
                </span>
                <p className="text-base font-extrabold leading-snug text-char-900">{article.title}</p>
                {article.author?.name && (
                  <p className="text-xs font-bold text-char-400">
                    {t.reporter}: <span className="text-char-500">{article.author.name}</span>
                  </p>
                )}
                <p className="max-h-40 overflow-y-auto rounded-2xl bg-char-50/60 p-3 text-sm leading-relaxed text-char-500">
                  {article.excerpt || article.content}
                </p>
              </div>

              <div className="mt-5">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-xs font-extrabold text-char-500">
                    {t.remarksLabel}
                  </label>
                  <span className="text-xs text-char-300">
                    {remarks.length}/{MAX_REMARKS}
                  </span>
                </div>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value.slice(0, MAX_REMARKS))}
                  onBlur={() => setTouched(true)}
                  placeholder={t.remarksPlaceholder}
                  rows={3}
                  className={`w-full rounded-2xl border px-3 py-2 text-sm transition-colors focus:outline-none ${
                    touched && remarksEmpty
                      ? 'border-red-300 focus:border-red-400'
                      : 'border-char-200 focus:border-primary'
                  }`}
                />
                {touched && remarksEmpty && (
                  <p className="mt-1 text-xs font-bold text-red-500">{t.remarksRequired}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer (sticky) */}
        <div className="flex flex-wrap justify-end gap-3 border-t border-char-50 px-6 py-4">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-2xl border border-char-200 px-4 py-2 text-sm font-extrabold text-char-600 transition-colors hover:bg-char-50 disabled:opacity-50"
          >
            {t.cancel}
          </button>

          {isDeleteMode ? (
            <button
              onClick={onDelete}
              disabled={loading}
              className="flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              {t.delete}
            </button>
          ) : (
            <>
              <button
                onClick={handleReject}
                disabled={loading}
                className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-extrabold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                {t.reject}
              </button>
              <button
                onClick={onApprove}
                disabled={loading}
                className="flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-extrabold text-white transition-colors hover:bg-primary-500 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                {t.approve}
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ReviewModal;